import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useLanguage, LANGUAGES } from '../context/LanguageContext';
import { Button } from '../components/ui/Button';
import { Input, Textarea } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { Check, ArrowRight, Lock, Shield, Server, FileText, CreditCard, Globe } from 'lucide-react';
import PhoneInput from 'react-phone-number-input';
import { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

const API_URL = 'https://backend-sites.dathent.com';

// Plan configurations
const PLANS = {
  auction: {
    id: 'website_with_subscription',
    setup: 500,
    subscription: 50,
    hasSubscription: true,
    commitment: 12,
  },
  one_time: {
    id: 'website_full_price',
    setup: 1500,
    subscription: 0,
    hasSubscription: false,
    commitment: 0,
  },
  one_time_support: {
    id: 'website_full_with_subscription',
    setup: 1500,
    subscription: 150,
    hasSubscription: true,
    commitment: 0,
  },
};

export function CheckoutPage() {
  const [searchParams] = useSearchParams();
  const { t, language } = useLanguage();
  const checkout = t('checkout');
  
  const planParam = searchParams.get('plan') || 'auction';
  const plan = PLANS[planParam] || PLANS.auction;
  
  const [currentStep, setCurrentStep] = useState(1);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsScrolled, setTermsScrolled] = useState(false);
  const [termsError, setTermsError] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    projectType: 'landing',
    description: '',
    deadline: 'normal',
    hasContent: false,
    additionalNotes: ''
  });
  
  const [validationErrors, setValidationErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [savedBriefId, setSavedBriefId] = useState(null);
  const [step2ButtonEnabled, setStep2ButtonEnabled] = useState(false);

  // Load saved data
  useEffect(() => {
    const savedData = localStorage.getItem('checkoutFormData');
    const savedId = localStorage.getItem('savedBriefId');
    const returnStep = localStorage.getItem('checkoutReturnStep');
    
    if (savedData) {
      try {
        setFormData(JSON.parse(savedData));
      } catch (error) {
        console.error('Failed to parse saved form data:', error);
      }
    }
    
    if (savedId) {
      setSavedBriefId(parseInt(savedId, 10));
    }
    
    // Restore step if returning from legal page
    if (returnStep) {
      setCurrentStep(parseInt(returnStep, 10));
      localStorage.removeItem('checkoutReturnStep'); // Clear after use
    }
  }, []);

  // Auto-save form data
  useEffect(() => {
    if (formData.name || formData.email || formData.phone) {
      localStorage.setItem('checkoutFormData', JSON.stringify(formData));
    }
  }, [formData]);

  // Enable Step 2 next button after 7 seconds
  useEffect(() => {
    if (currentStep === 2) {
      setStep2ButtonEnabled(false);
      const timer = setTimeout(() => {
        setStep2ButtonEnabled(true);
      }, 7000); // 7 seconds
      return () => clearTimeout(timer);
    }
  }, [currentStep]);

  // Auto-save brief to backend when Step 1 is complete
  useEffect(() => {
    const saveStep1Brief = async () => {
      const hasName = formData.name.trim().length > 0;
      const hasEmail = formData.email.trim().length > 0;
      const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email);
      const hasPhone = formData.phone && formData.phone.trim().length > 0;
      const isPhoneValid = formData.phone ? isValidPhoneNumber(formData.phone) : false;
      
      if (currentStep === 1 && hasName && hasEmail && isEmailValid && hasPhone && isPhoneValid) {
        try {
          const briefResponse = await fetch(`${API_URL}/api/brief`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              brief_id: savedBriefId,
              name: formData.name,
              email: formData.email,
              phone: formData.phone,
              company: formData.company,
              project_type: formData.projectType,
              description: formData.description,
              budget: 'custom',
              timeline: formData.deadline,
              has_design: formData.hasContent,
              additional_info: formData.additionalNotes,
              package_id: plan.id,
              language
            })
          });

          const briefResult = await briefResponse.json();
          if (briefResult.id && briefResult.id !== savedBriefId) {
            setSavedBriefId(briefResult.id);
            localStorage.setItem('savedBriefId', briefResult.id.toString());
          }
        } catch (error) {
          console.error('Failed to auto-save brief:', error);
        }
      }
    };

    // Debounce to avoid too many requests
    const timer = setTimeout(saveStep1Brief, 1000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.name, formData.email, formData.phone, currentStep, savedBriefId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (validationErrors[name] && touchedFields[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleBlur = (fieldName) => {
    setTouchedFields(prev => ({ ...prev, [fieldName]: true }));
    
    // Validate field on blur
    if (fieldName === 'name' && !formData.name.trim()) {
      setValidationErrors(prev => ({ ...prev, name: checkout.errors.nameRequired }));
    } else if (fieldName === 'email') {
      if (!formData.email.trim()) {
        setValidationErrors(prev => ({ ...prev, email: checkout.errors.emailRequired }));
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        setValidationErrors(prev => ({ ...prev, email: checkout.errors.emailInvalid }));
      } else {
        setValidationErrors(prev => ({ ...prev, email: '' }));
      }
    } else if (fieldName === 'phone') {
      if (!formData.phone.trim()) {
        setValidationErrors(prev => ({ ...prev, phone: checkout.errors.phoneRequired }));
      } else if (!isValidPhoneNumber(formData.phone)) {
        setValidationErrors(prev => ({ ...prev, phone: checkout.errors.phoneInvalid }));
      } else {
        setValidationErrors(prev => ({ ...prev, phone: '' }));
      }
    } else if (fieldName === 'description') {
      if (!formData.description.trim()) {
        setValidationErrors(prev => ({ ...prev, description: checkout.errors.descriptionRequired }));
      } else {
        setValidationErrors(prev => ({ ...prev, description: '' }));
      }
    }
  };

  const validateStep1 = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = checkout.errors.nameRequired;
    if (!formData.email.trim()) errors.email = checkout.errors.emailRequired;
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = checkout.errors.emailInvalid;
    }
    if (!formData.phone.trim()) errors.phone = checkout.errors.phoneRequired;
    else if (!isValidPhoneNumber(formData.phone)) {
      errors.phone = checkout.errors.phoneInvalid;
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateStep2 = () => {
    const errors = {};
    if (!formData.description.trim()) errors.description = checkout.errors.descriptionRequired;
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Check if Step 1 form is valid for button state
  const isStep1Valid = () => {
    const hasName = formData.name.trim().length > 0;
    const hasEmail = formData.email.trim().length > 0;
    const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email);
    const hasPhone = formData.phone && formData.phone.trim().length > 0;
    const isPhoneValid = formData.phone ? isValidPhoneNumber(formData.phone) : false;
    
    return hasName && hasEmail && isEmailValid && hasPhone && isPhoneValid;
  };

  // Save/update brief to backend
  const saveBriefToBackend = async () => {
    try {
      const briefResponse = await fetch(`${API_URL}/api/brief`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          brief_id: savedBriefId,
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          project_type: formData.projectType,
          description: formData.description,
          budget: 'custom',
          timeline: formData.deadline,
          has_design: formData.hasContent,
          additional_info: formData.additionalNotes,
          package_id: plan.id,
          language
        })
      });

      const briefResult = await briefResponse.json();
      if (briefResult.id) {
        setSavedBriefId(briefResult.id);
        localStorage.setItem('savedBriefId', briefResult.id.toString());
      }
      return briefResult.id || savedBriefId;
    } catch (error) {
      console.error('Failed to save brief:', error);
      return savedBriefId;
    }
  };

  const handleNext = async () => {
    if (currentStep === 1) {
      if (!validateStep1()) {
        // Mark all fields as touched to show errors
        setTouchedFields({ name: true, email: true, phone: true });
        
        // Find first invalid field and focus it
        const fieldsOrder = ['name', 'email', 'phone'];
        for (const field of fieldsOrder) {
          if (validationErrors[field]) {
            const element = document.querySelector(`[name="${field}"]`) || 
                          document.querySelector('.phone-input input');
            if (element) {
              element.focus();
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            break;
          }
        }
        return;
      }
      // Save brief after Step 1 validation
      await saveBriefToBackend();
    }
    if (currentStep === 2) {
      if (!validateStep2()) return;
      // Update brief with Step 2 data
      await saveBriefToBackend();
    }
    setCurrentStep(prev => prev + 1);
    window.scrollTo(0, 0);
  };

  // Handle Enter key press in Step 1 fields
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && currentStep === 1) {
      e.preventDefault();
      handleNext();
    }
  };

  const handleStepBack = () => {
    setCurrentStep(prev => prev - 1);
    window.scrollTo(0, 0);
  };



  const handleLegalLinkClick = (e) => {
    // Save checkout state before navigating to legal pages
    localStorage.setItem('checkoutReturnTo', window.location.pathname + window.location.search);
    localStorage.setItem('checkoutReturnStep', currentStep.toString());
  };

  const handleTermsClick = (e) => {
    if (!termsAccepted) {
      e.preventDefault();
      setTermsScrolled(false);
      setShowTermsModal(true);
      setTermsError(false);
    }
  };

  const handleTermsScroll = (e) => {
    const element = e.target;
    const scrolledToBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 50;
    const noScrollNeeded = element.scrollHeight <= element.clientHeight + 10;
    if (scrolledToBottom || noScrollNeeded) {
      setTermsScrolled(true);
    }
  };

  const handleAcceptTerms = () => {
    setTermsAccepted(true);
    setShowTermsModal(false);
    setTermsError(false);
  };

  const handleSubmit = async () => {
    if (!termsAccepted) {
      setTermsError(true);
      return;
    }

    setIsSubmitting(true);

    try {
      // Save/update brief
      const briefResponse = await fetch(`${API_URL}/api/brief`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          brief_id: savedBriefId,
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          project_type: formData.projectType,
          description: formData.description,
          budget: 'custom',
          timeline: formData.deadline,
          has_design: formData.hasContent,
          additional_info: formData.additionalNotes,
          package_id: plan.id,
          language
        })
      });

      const briefResult = await briefResponse.json();
      const briefId = briefResult.id || savedBriefId;

      // Create checkout session
      const checkoutResponse = await fetch(`${API_URL}/api/checkout/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          package_id: plan.id,
          origin_url: window.location.origin,
          metadata: {
            brief_id: briefId,
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            company: formData.company
          },
          customer_details: {
            email: formData.email
          }
        })
      });

      const { url } = await checkoutResponse.json();
      
      // Don't clear localStorage here - user might click back in Stripe
      // It will be cleared on success page instead
      
      // Redirect to Stripe
      window.location.href = url;
    } catch (error) {
      console.error('Checkout error:', error);
      setIsSubmitting(false);
    }
  };

  const renderProgressBar = () => (
    <div className="mb-12">
      <div className="flex items-center justify-between max-w-3xl mx-auto">
        {[1, 2, 3].map((step) => (
          <div key={step} className="flex items-center flex-1">
            <div className="flex flex-col items-center flex-1">
              <div className={`
                w-12 h-12 rounded-full flex items-center justify-center font-bold text-base transition-all
                ${currentStep > step ? 'bg-primary text-white shadow-lg shadow-primary/30' : ''}
                ${currentStep === step ? 'bg-primary text-white shadow-lg shadow-primary/30 ring-4 ring-primary/20' : ''}
                ${currentStep < step ? 'border-2 border-slate-600 text-slate-500 bg-slate-800' : ''}
              `}>
                {currentStep > step ? <Check size={22} /> : step}
              </div>
              <span className={`text-sm mt-3 font-semibold ${currentStep >= step ? 'text-white' : 'text-slate-500'}`}>
                {checkout.steps[`step${step}`]}
              </span>
            </div>
            {step < 3 && (
              <div className={`h-1 flex-1 mx-4 rounded-full transition-all ${currentStep >= step ? 'bg-primary shadow-sm shadow-primary/50' : 'bg-slate-700'}`} />
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const renderOrderSummary = () => {
    const getPlanName = () => {
      if (planParam === 'auction') return checkout.summary.planPromo || 'Promotional (12-month commitment)';
      if (planParam === 'one_time') return checkout.summary.planOneTime || 'One-time payment';
      if (planParam === 'one_time_support') return checkout.summary.planPremium || 'Premium with support';
      return '';
    };

    return (
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 flex flex-col sticky top-6 lg:h-full">
        {/* Compact Step Indicator */}
        <div className="mb-4 pb-4 border-b border-slate-700">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-400 uppercase tracking-wider">
              {checkout.steps?.[`step${currentStep}`] || `Step ${currentStep}`}
            </span>
            <span className="text-xs font-semibold text-primary">{currentStep} / 3</span>
          </div>
          {/* Mini progress dots */}
          <div className="flex gap-2">
            {[1, 2, 3].map((step) => (
              <div
                key={step}
                className={`h-1 flex-1 rounded-full transition-all ${
                  currentStep >= step ? 'bg-primary' : 'bg-slate-700'
                }`}
              />
            ))}
          </div>
        </div>
        
        <h3 className="font-semibold text-lg text-white mb-4">{checkout.summary.title}</h3>
        
        <div className="bg-primary/10 border border-primary/30 rounded-lg p-3 mb-4">
          <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">{checkout.summary.planLabel || 'Plan'}</p>
          <p className="text-sm text-white font-semibold">{getPlanName()}</p>
        </div>
        
        <div className="space-y-3 pt-2 border-t border-slate-700 mb-4">
          <div className="flex justify-between text-sm">
            <span className="text-slate-300">{checkout.summary.websiteSetup}</span>
            <span className="text-white font-semibold">€{plan.setup}</span>
          </div>
          
          {plan.hasSubscription && (
            <div className="flex justify-between text-sm">
              <div className="flex flex-col">
                <span className="text-slate-300">{checkout.summary.support}</span>
                <span className="text-xs text-slate-500">{checkout.summary.supportStartNote || 'starting month 2'}</span>
              </div>
              <span className="text-white font-semibold">€{plan.subscription}/{checkout.summary.month}</span>
            </div>
          )}
        </div>
        
        <div className="pt-3 border-t border-slate-700">
          <div className="flex justify-between items-baseline">
            <span className="text-slate-400 text-sm">{checkout.summary.totalToday}</span>
            <span className="text-2xl font-bold text-primary">€{plan.setup}</span>
          </div>
        </div>
        
        {plan.hasSubscription && planParam === 'auction' && (
          <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-3">
            <p className="text-xs text-blue-200">
              {checkout.summary.subscriptionActivates || 'Subscription €50/mo activates after 30 days.'}
            </p>
            <p className="text-xs text-blue-300 font-medium mt-1">
              {checkout.summary.minimumTerm || 'Minimum term — 12 months.'}
            </p>
          </div>
        )}
        
        {plan.hasSubscription && planParam === 'one_time_support' && (
          <div className="bg-slate-700/30 border border-slate-600/50 rounded-lg p-3">
            <p className="text-xs text-slate-300">
              {checkout.summary.premiumSupportNote}
            </p>
          </div>
        )}
        
        {/* Trust footer inside Order Summary */}
        <div className="mt-auto pt-6 border-t border-slate-700">
          <div className="flex flex-col gap-2 text-xs text-slate-500">
            <div className="flex items-center gap-2">
              <Shield size={14} className="text-primary flex-shrink-0" />
              <span>Stripe Secure</span>
            </div>
            <div className="flex items-center gap-2">
              <Lock size={14} className="text-primary flex-shrink-0" />
              <span>SSL Secured</span>
            </div>
            <div className="flex items-center gap-2">
              <FileText size={14} className="text-primary flex-shrink-0" />
              <span>GDPR Compliant</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderStep1 = () => (
    <div>
        <div className="mb-4">
            <h2 className="text-2xl lg:text-3xl font-bold text-white mb-2">{checkout.step1.title}</h2>
            <p className="text-slate-300 text-base">{checkout.step1.subtitle}</p>
            </div>
        <div className="grid lg:grid-cols-[1fr_400px] gap-6 lg:items-start">
        <div className="flex flex-col">
            
            
            <div className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/60 rounded-2xl shadow-2xl p-6 lg:p-8 space-y-4 lg:h-full flex flex-col">
            <Input
                label={checkout.step1.name}
                name="name"
                value={formData.name}
                onChange={handleChange}
                onBlur={() => handleBlur('name')}
                error={touchedFields.name ? validationErrors.name : ''}
                placeholder="John Doe"
                required
            />
            
            <Input
                label={checkout.step1.email}
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={() => handleBlur('email')}            onKeyPress={handleKeyPress}            error={touchedFields.email ? validationErrors.email : ''}
                placeholder="john@example.com"
                required
            />
            
            <div>
                <label className="block text-sm font-semibold text-slate-200 mb-2">
                {checkout.step1.phone} <span className="text-primary">*</span>
                </label>
                <div className="phone-input-wrapper" onKeyPress={handleKeyPress}>
                <PhoneInput
                    international
                    defaultCountry="ES"
                    value={formData.phone}
                    onChange={(value) => {
                    setFormData(prev => ({ ...prev, phone: value || '' }));
                    if (validationErrors.phone && touchedFields.phone) {
                        setValidationErrors(prev => ({ ...prev, phone: '' }));
                    }
                    }}
                    onBlur={() => handleBlur('phone')}
                    placeholder="+34 600 000 000"
                    className="phone-input"
                />
                </div>
                {touchedFields.phone && validationErrors.phone && (
                <p className="mt-2 text-sm text-red-400">{validationErrors.phone}</p>
                )}
            </div>
            
            <Input
                label={checkout.step1.company}
                name="company"
                value={formData.company}
                onChange={handleChange}
                placeholder="Acme Inc. (optional)"
            />
            
            <div className="pt-3 mt-auto border-t border-slate-700">
                <p className="text-xs text-slate-500 leading-relaxed opacity-75">
                {checkout.step1.managerNote || 'Our manager will contact you after payment to collect project details.'}
                </p>
            </div>
            </div>
            
            {/* Next Button - inside left container */}
            <div className="flex justify-end mt-4">
            <Button
                type="button"
                onClick={handleNext}
                disabled={!isStep1Valid()}
                className="flex items-center gap-2"
            >
                {checkout.btnNext}
                <ArrowRight size={18} />
            </Button>
            </div>
        </div>
        
        <div className="lg:block hidden">
            {renderOrderSummary()}
        </div>
        </div>
    </div>
  );

  const renderStep2 = () => (
    <div>
        <div className="mb-4">
            <h2 className="text-2xl lg:text-3xl font-bold text-white mb-2">{checkout.step2.title}</h2>
            <p className="text-slate-300 text-base">{checkout.step2.subtitle}</p>
            </div>
        <div className="grid lg:grid-cols-[1fr_400px] gap-6 lg:items-start">
        <div className="flex flex-col">
            
            <div className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/60 rounded-2xl shadow-2xl p-6 lg:p-8 space-y-4 flex-1">
            <div>
                <label className="block text-sm font-semibold text-slate-200 mb-2">
                {checkout.step2.projectType} <span className="text-primary">*</span>
                </label>
                <select
                name="projectType"
                value={formData.projectType}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
                >
                {Object.entries(checkout.step2.projectTypes).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                ))}
                </select>
            </div>
            
            <Textarea
                label={checkout.step2.description}
                name="description"
                value={formData.description}
                onChange={handleChange}
                onBlur={() => handleBlur('description')}
                error={touchedFields.description ? validationErrors.description : ''}
                rows={3}
                placeholder="Опишите детали вашего проекта..."
                required
            />
            
            <div>
                <label className="block text-sm font-semibold text-slate-200 mb-2">
                {checkout.step2.deadline}
                </label>
                <select
                name="deadline"
                value={formData.deadline}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
                >
                {Object.entries(checkout.step2.deadlines).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                ))}
                </select>
            </div>
            
            <label className="flex items-start gap-3 cursor-pointer group p-3 bg-slate-800/50 rounded-lg border border-slate-700 hover:border-primary/50 transition-all">
                <input
                type="checkbox"
                name="hasContent"
                checked={formData.hasContent}
                onChange={handleChange}
                className="mt-0.5 w-5 h-5 rounded border-slate-600 bg-slate-800 text-primary focus:ring-primary focus:ring-offset-slate-900"
                />
                <span className="text-sm text-slate-200 font-medium">{checkout.step2.hasContent}</span>
            </label>
            
            <Textarea
                label={checkout.step2.additionalNotes}
                name="additionalNotes"
                value={formData.additionalNotes}
                onChange={handleChange}
                rows={2}
                placeholder="Любая дополнительная информация..."
            />
            </div>
            
            {/* Navigation Buttons - inside left container */}
            <div className="flex justify-between items-center mt-4">
            <Button
                type="button"
                variant="outline"
                onClick={handleStepBack}
            >
                {checkout.btnBack}
            </Button>
            <Button
                type="button"
                onClick={handleNext}
                disabled={!step2ButtonEnabled}
                className="flex items-center gap-2"
                title={!step2ButtonEnabled ? 'Please wait a moment...' : ''}
            >
                {!step2ButtonEnabled && (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                )}
                {checkout.btnNext}
                <ArrowRight size={18} />
            </Button>
            </div>
        </div>
        
        <div className="lg:block hidden">
            {renderOrderSummary()}
        </div>
        </div>
    </div>
  );

  const renderStep3 = () => (
    <div>
        <div>
            <h2 className="text-2xl lg:text-3xl font-bold text-white mb-2">{checkout.step3.title}</h2>
            <p className="text-slate-300 text-base">{checkout.step3.subtitle}</p>
        </div>
        <div className="grid lg:grid-cols-[1fr_400px] gap-6">
        <div className="space-y-4 lg:space-y-6">
            
            <div className="bg-slate-800/30 border border-slate-700 rounded-xl p-6 space-y-4">
            <h3 className="font-semibold text-white">{checkout.step3.legalTitle}</h3>
            
            <label className="flex items-start gap-3 cursor-pointer group">
                <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                onClick={handleTermsClick}
                className="mt-1 w-5 h-5 rounded border-slate-600 bg-slate-800 text-primary focus:ring-primary focus:ring-offset-slate-900 cursor-pointer"
                />
                <span className="text-sm text-slate-300">
                {termsAccepted ? (
                    checkout.step3.termsAccepted
                ) : (
                    <span className="text-primary font-medium">
                    {checkout.step3.termsClickToView}
                    </span>
                )}
                </span>
            </label>
            
            {termsError && (
                <p className="text-sm text-red-400">{checkout.errors.termsRequired}</p>
            )}
            </div>
            
            {/* Navigation Buttons */}
            <div className="flex justify-between items-center gap-4">
            <Button
                type="button"
                variant="outline"
                onClick={handleStepBack}
            >
                {checkout.btnBack}
            </Button>
            <Button
                onClick={handleSubmit}
                disabled={isSubmitting || !termsAccepted}
                className="flex-1 py-4 text-lg"
            >
                {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {checkout.step3.processing}
                </span>
                ) : (
                <span className="flex items-center justify-center gap-2">
                    <CreditCard size={20} />
                    {checkout.step3.payButton}
                </span>
                )}
            </Button>
            </div>
            <p className="text-xs text-center text-slate-400 mt-2">
                {checkout.step3.securePayment}
            </p>
            
            <div className="grid grid-cols-2 gap-3 text-xs text-slate-400">
            <div className="flex items-center gap-2">
                <Lock size={14} className="text-primary flex-shrink-0" />
                <span>{checkout.trust.stripe}</span>
            </div>
            <div className="flex items-center gap-2">
                <Shield size={14} className="text-primary flex-shrink-0" />
                <span>{checkout.trust.ssl}</span>
            </div>
            <div className="flex items-center gap-2">
                <Server size={14} className="text-primary flex-shrink-0" />
                <span>{checkout.trust.noStorage}</span>
            </div>
            <div className="flex items-center gap-2">
                <FileText size={14} className="text-primary flex-shrink-0" />
                <span>{checkout.trust.euCompany}</span>
            </div>
            </div>
        </div>
        
        <div className="lg:block hidden">
            {renderOrderSummary()}
        </div>
        </div>
    </div>
  );

  const renderTermsModal = () => (
    <Modal 
      isOpen={showTermsModal} 
      onClose={() => setShowTermsModal(false)}
      className="max-w-4xl max-h-[90vh] flex flex-col"
    >
      <div className="p-8 pb-0">
        <h2 className="font-heading text-2xl font-bold mb-6 text-white">
          {checkout.terms.title}
        </h2>
      </div>
        
      <div 
        className="prose prose-invert prose-slate max-w-none space-y-6 overflow-y-auto px-8 pb-4"
        style={{ maxHeight: 'calc(90vh - 220px)' }}
        onScroll={handleTermsScroll}
      >
        {checkout.terms.content}
      </div>

      <div className="border-t border-slate-700 p-8 pt-4 bg-slate-900">
        {!termsScrolled && (
          <p className="text-xs text-amber-400 text-center mb-3 animate-pulse">
            {checkout.terms.scrollNotice}
          </p>
        )}
        <div className="flex gap-3 justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => setShowTermsModal(false)}
          >
            {checkout.terms.closeButton}
          </Button>
          <Button
            type="button"
            onClick={handleAcceptTerms}
            disabled={!termsScrolled}
            className={!termsScrolled ? 'opacity-50 cursor-not-allowed' : ''}
          >
            <Check size={18} className="mr-2" />
            {checkout.terms.acceptButton}
          </Button>
        </div>
      </div>
    </Modal>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col">
      {/* Minimal Header */}
      <header className="border-b border-slate-800 bg-slate-900/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link 
              to={`/${language}`}
              className="flex items-center gap-3 hover:opacity-80 transition-opacity"
            >
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center font-bold text-white">
                D
              </div>
              <div className="flex flex-col">
                <span className="font-bold text-white text-lg leading-none">DATHENT</span>
                <span className="text-xs text-slate-400 leading-none mt-0.5">{checkout.secureCheckout || 'Secure Checkout'}</span>
              </div>
            </Link>
          </div>
          
          <div className="flex items-center gap-2">
            <Globe size={16} className="text-slate-400" />
            <select
              value={language}
              onChange={(e) => window.location.href = `/${e.target.value}/checkout?plan=${planParam}`}
              className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-white focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
            >
              {LANGUAGES.map(lang => (
                <option key={lang.code} value={lang.code}>
                  {lang.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        <div className="max-w-6xl mx-auto px-4 py-6 lg:py-8">
          {/* Show progress bar only on mobile */}
          <div className="lg:hidden">
            {renderProgressBar()}
          </div>
          
          <div className="mt-4 lg:mt-0">
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}
          </div>
          
          {/* Mobile sticky summary for step 3 */}
          {currentStep === 3 && (
            <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-slate-900 border-t border-slate-700 p-4 z-40">
              <div className="flex justify-between items-center mb-3">
                <span className="text-slate-400 text-sm">{checkout.summary.totalToday}</span>
                <span className="text-xl font-bold text-primary">€{plan.setup}</span>
              </div>
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting || !termsAccepted}
                className="w-full"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {checkout.step3.processing}
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <CreditCard size={20} />
                    {checkout.step3.payButton}
                  </span>
                )}
              </Button>
            </div>
          )}
        </div>
      </main>

      {/* Minimal Footer */}
      <footer className="border-t border-slate-800 bg-slate-900/30 mt-auto">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-slate-400">
              <Link 
                to={`/${language}/terms`}
                onClick={handleLegalLinkClick}
                className="hover:text-primary transition-colors"
              >
                {t('footer.legal.terms')}
              </Link>
              <Link 
                to={`/${language}/privacy`}
                onClick={handleLegalLinkClick}
                className="hover:text-primary transition-colors"
              >
                {t('footer.legal.privacy')}
              </Link>
              <Link 
                to={`/${language}/refund`}
                onClick={handleLegalLinkClick}
                className="hover:text-primary transition-colors"
              >
                {t('footer.legal.refund')}
              </Link>
              <span className="text-slate-600">•</span>
              <span className="text-slate-500">© 2025 DATHENT</span>
            </div>
            
            <div className="flex items-center gap-4 text-xs text-slate-500">
              <div className="flex items-center gap-1.5">
                <Shield size={14} className="text-primary" />
                <span>Stripe Secure</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Lock size={14} className="text-primary" />
                <span>SSL Secured</span>
              </div>
              <div className="flex items-center gap-1.5">
                <FileText size={14} className="text-primary" />
                <span>GDPR Compliant</span>
              </div>
            </div>
          </div>
        </div>
      </footer>
      
      {renderTermsModal()}
    </div>
  );
}
